import axios from 'axios'
import {API_URL} from '../../../../_metronic/helpers'
import {BillingMethod, isContactTariff, ServiceTariff, tariffParts} from './serviceApi'

// The business's value-added services in one list (backend 1.0.0.639+): api/business-services/catalog, and
// «فعال‌سازی» of a service whose page has no save of its own (the proforma, the card reader's automatic invoices).

export type ServiceFeatureKey =
  | 'buyerLink'
  | 'buyerLinkDecision'
  | 'buyerLinkChat'
  | 'buyerLinkReceipt'
  | 'referralCorrection'
  | 'referralCancellation'
  | 'referralReturn'
  | 'buyerReaction'
  | 'article6'
  | 'sendError'
  | 'batchSent'
  | 'periodicReport'
  | 'onlinePayment'
  | 'proforma'
  | 'posAutoInvoice'
  | 'extraUser'
  | 'printTemplate'
  | 'bulkPdf'
  | 'loginRestriction'
  | 'taxWorkspaceAlerts'
  | 'notificationSms'
  | 'taxWorkspaceAutoApprove'
  | 'bulkExcel'
  | 'bulkCancel'
  | 'taxFolderZip'
  | 'stuffidCategories'
  | 'costPriceForm'
  | 'advancedCostPriceForm'
  | 'buyerTaxFileInquiry'
  | 'accountingReturns'
  | 'supplyChain'
  | string

/**
 * No longer value-added services: «بررسی وضعیت کارپوشه پیش از ارسال» is free and always on for everyone since
 * 2026-10-18 (backend 1.0.0.672+ leaves it out of the catalog; an older one still lists it).
 */
export const RETIRED_FEATURES: readonly string[] = ['taxFileSendCheck']

/**
 * The services only an administrator turns on and off, with no tariff and no end (backend 1.0.0.759+, user decision
 * 2026-10-29: «تعریف زنجیرهٔ تأمین برق», whose price is in the CRM contract). The catalog says so itself (adminOnly);
 * this list answers for the administrator's pages, whose rows do not carry the flag.
 */
export const ADMIN_ONLY_FEATURES: readonly string[] = ['supplyChain']

export const isAdminOnlyFeature = (feature?: string | null, adminOnly?: boolean | null): boolean =>
  Boolean(adminOnly) || ADMIN_ONLY_FEATURES.includes(String(feature || ''))

export type CatalogFeature = {
  tariff: ServiceTariff
  /** Nothing to pay before using it: free, activated, used before the tariffs, or free for this business. */
  active: boolean
  /** What one use takes now (after the free list, gifts and codes); null for a commission on an amount. */
  useFee?: number | null
}

export type ServiceCatalog = {
  available: boolean
  companyId: number
  canChange: boolean
  features: CatalogFeature[]
}

export const serviceCatalogKey = (companyId: number) => ['business-services', 'catalog', companyId]

export const getServiceCatalog = async (companyId: number): Promise<ServiceCatalog> => {
  const catalog = (
    await axios.get<ServiceCatalog>(`${API_URL}/business-services/catalog`, {params: {companyId}})
  ).data
  return catalog?.features
    ? {
        ...catalog,
        features: catalog.features.filter(
          (item) => !RETIRED_FEATURES.includes(item.tariff.feature)
        ),
      }
    : catalog
}

/**
 * «فعال‌سازی»; with renew (backend 1.0.0.643+) «تمدید» of a subscription that ends soon, from its end. periodMonths
 * (backend 1.0.0.675+): the subscription period picked among the tariff's; left out, the one bought last or the tariff's own.
 * method and autoRenew (backend 1.0.0.690+): the way the business pays for a «ترکیبی» service, and «تمدید خودکار» of
 * the subscription it takes now.
 */
export const activateServiceFeature = async (
  companyId: number,
  feature: ServiceFeatureKey,
  confirmCharge: boolean,
  renew = false,
  periodMonths?: number,
  method?: BillingMethod,
  autoRenew?: boolean
): Promise<ServiceCatalog> =>
  (
    await axios.post<ServiceCatalog>(
      `${API_URL}/business-services/features/${encodeURIComponent(feature)}/activate`,
      {
        companyId,
        confirmCharge,
        renew,
        ...(periodMonths !== undefined ? {periodMonths} : {}),
        ...(method ? {method} : {}),
        ...(autoRenew !== undefined ? {autoRenew} : {}),
      }
    )
  ).data

/** «تمدید خودکار» of the business's «ترکیبی» subscription, on or off (backend 1.0.0.690+). */
export const setServiceAutoRenew = async (
  companyId: number,
  feature: ServiceFeatureKey,
  autoRenew: boolean
): Promise<ServiceCatalog> =>
  (
    await axios.post<ServiceCatalog>(
      `${API_URL}/business-services/features/${encodeURIComponent(feature)}/auto-renew`,
      {
        companyId,
        autoRenew,
      }
    )
  ).data

/**
 * «غیرفعال کردن» of the business's own service (backend 1.0.0.651+): it stops now, nothing is given back, and its own
 * «فعال‌سازی» turns it on again at the day's tariff. Sent once the user has read that (switchOffConsequence).
 */
export const switchOffServiceFeature = async (
  companyId: number,
  feature: ServiceFeatureKey
): Promise<ServiceCatalog> =>
  (
    await axios.post<ServiceCatalog>(
      `${API_URL}/business-services/features/${encodeURIComponent(feature)}/switch-off`,
      {
        companyId,
        confirmed: true,
      }
    )
  ).data

/** What switching a service off means, in the confirmation (the server's own words). */
export const switchOffConsequence = (label: string) =>
  `«${label}» از همین لحظه برای این کسب‌وکار کار نمی‌کند و هزینهٔ پرداخت‌شده برنمی‌گردد. تنظیمات آن می‌ماند و اگر بعداً دوباره فعالش کنید، هزینهٔ فعال‌سازی طبق تعرفهٔ همان روز کم می‌شود.`

/**
 * The same for a «تماس بگیرید» service the business has access to: switching it off keeps the access, so it may be
 * turned on again, free, until the access ends.
 */
export const contactSwitchOffConsequence = (label: string, until?: string | null) =>
  `«${label}» از همین لحظه برای این کسب‌وکار کار نمی‌کند. تنظیمات آن و دسترسی‌ای که پشتیبانی داده است${
    until ? ` (تا ${until})` : ''
  } می‌ماند و تا پایان دسترسی می‌توانید دوباره فعالش کنید.`

/**
 * What stops, and what stays, when a service is switched off (backend 1.0.0.651+), for the confirmation of the business
 * and of an administrator: what was already made keeps working, only new uses stop.
 */
export const SWITCH_OFF_EFFECTS: Record<string, string> = {
  buyerLinkDecision:
    'لینک‌های تازه بدون امکان تأیید یا رد خریدار ساخته می‌شوند؛ لینک‌های پیشین همان‌طور می‌مانند.',
  buyerLinkChat: 'گفتگو در لینک‌های تازه روشن نمی‌شود؛ گفتگوهای پیشین می‌مانند.',
  buyerLinkReceipt:
    'لینک‌های تازه بدون امکان ثبت رسید پرداخت ساخته می‌شوند؛ رسیدهای ثبت‌شده می‌مانند.',
  referralCorrection:
    'صدور صورتحساب اصلاحی تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد؛ اصلاحی‌های ثبت‌شده می‌مانند.',
  referralCancellation:
    'ابطال صورتحساب‌ها متوقف نمی‌شود؛ فقط هزینه‌ای برای آن گرفته نمی‌شود تا سرویس دوباره فعال شود.',
  referralReturn:
    'صدور برگشت از فروش تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد؛ برگشت‌های ثبت‌شده می‌مانند.',
  buyerLink:
    'لینک‌هایی که پیش‌تر فرستاده شده‌اند باز می‌مانند؛ لینک خودکار فرستاده نمی‌شود و ساخت لینک تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  buyerReaction: 'اعلان تأیید یا رد صورتحساب‌ها به‌دست خریدار فرستاده نمی‌شود.',
  article6: 'اعلان عبور از حد مجاز ماده ۶ فرستاده نمی‌شود.',
  sendError: 'اعلان خطای ارسال صورتحساب فرستاده نمی‌شود.',
  batchSent: 'اعلان پایان ارسال گروهی فرستاده نمی‌شود.',
  periodicReport: 'گزارش‌های دوره‌ای ساخته و فرستاده نمی‌شوند؛ زمان‌بندی‌ها می‌مانند.',
  taxWorkspaceAlerts: 'کارپوشه برای اعلان‌ها بررسی نمی‌شود و اعلانی فرستاده نمی‌شود.',
  notificationSms: 'اعلان‌ها دیگر پیامک نمی‌شوند و فقط در پنل، تلگرام و اعلان مرورگر می‌رسند.',
  taxWorkspaceAutoApprove: 'هیچ صورتحساب خریدی خودکار تأیید نمی‌شود؛ قاعدهٔ تأیید خودکار می‌ماند.',
  onlinePayment:
    'خریداران دیگر نمی‌توانند صورتحساب‌ها و پیش‌فاکتورهای این کسب‌وکار را آنلاین بپردازند.',
  proforma: 'پیش‌فاکتور تازه ثبت نمی‌شود؛ پیش‌فاکتورهای ثبت‌شده می‌مانند.',
  posAutoInvoice:
    'صدور صورتحساب خودکار تازه از فروش‌های کارتخوان به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  extraUser:
    'کاربران فعلی می‌مانند؛ افزودن کاربر بیش از تعداد رایگان به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  printTemplate:
    'قالب‌های چاپ فعلی می‌مانند؛ ساخت قالب بیش از تعداد رایگان به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  loginRestriction:
    'محدودیت‌های ورودی که ثبت شده‌اند همچنان اعمال می‌شوند؛ ثبت یا تغییر محدودیت تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  bulkPdf: 'خروجی PDF گروهی تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  bulkExcel: 'خروجی اکسل انبوه تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  bulkCancel: 'ابطال گروهی تازه به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  taxFolderZip: 'ورود صورتحساب از ZIP کارپوشه به فعال‌سازی دوبارهٔ سرویس نیاز دارد.',
  stuffidCategories:
    'مرور شناسه‌های کالا/خدمت به تفکیک دسته‌بندی برای کاربران کسب‌وکار بسته می‌شود؛ جست‌وجوی ساده رایگان می‌ماند.',
  costPriceForm:
    'صدور و ویرایش صورتحساب با «فرم قیمت تمام‌شده» بسته می‌شود؛ صورتحساب‌های صادرشده می‌مانند و فرم کامل همچنان در دسترس است.',
  advancedCostPriceForm:
    'صدور و ویرایش صورتحساب با «فرم قیمت تمام‌شده پیشرفته» بسته می‌شود؛ صورتحساب‌های صادرشده می‌مانند و فرم کامل همچنان در دسترس است.',
  accountingReturns:
    'برگشت چندمرجعی دیگر جدا ثبت و برگشت تازه‌ای تجمیع نمی‌شود؛ گروه‌های در انتظار سر وقت ثبت می‌شوند و برگشت‌های ثبت‌شده می‌مانند.',
  buyerTaxFileInquiry:
    'پرونده مالیاتی خریداران استعلام نمی‌شود؛ صورتحساب نوع ۱ برای خریدار حقیقیِ بدون شماره اقتصادی ثبت نمی‌شود و «افزودن خریدار» بدون استعلام ادامه پیدا می‌کند.',
  supplyChain:
    'کسب‌وکار درخواست تازه‌ای برای تعریف زنجیره ثبت نمی‌کند و درخواست‌های بازش ویرایش نمی‌شود؛ زنجیره‌های تعریف‌شده و لیست‌های فرستاده‌شده می‌مانند.',
}

export const switchOffEffect = (feature: ServiceFeatureKey): string | undefined =>
  SWITCH_OFF_EFFECTS[feature]

/**
 * Whether the business may switch the service off: it is on (not by a package's cover alone) and not off already. Never
 * a service only an administrator turns on and off.
 */
export const canSwitchOff = (item?: CatalogFeature | null) =>
  Boolean(
    item &&
      item.tariff.activated &&
      !item.tariff.switchedOff &&
      !isAdminOnlyFeature(item.tariff.feature, item.tariff.adminOnly)
  )

export const findFeature = (catalog: ServiceCatalog | undefined, feature: ServiceFeatureKey) =>
  catalog?.features.find((item) => item.tariff.feature === feature)

/** Whether a tariff takes anything at all (an activation, a use or a commission). */
export const isPaidTariff = (tariff?: ServiceTariff | null) => {
  if (!tariff) return false
  const parts = tariffParts(tariff)
  return (
    parts.activation > 0 ||
    parts.use > 0 ||
    Boolean(tariff.usePercent && tariff.usePercent > 0) ||
    Boolean(tariff.dailyLimit) ||
    // A service in the business's package shows what the package gives it.
    Boolean(tariff.package)
  )
}

/** Whether a service is shown with its terms: it takes something, or it is «تماس بگیرید» (support turns it on). */
export const isPricedTariff = (tariff?: ServiceTariff | null) =>
  isPaidTariff(tariff) || isContactTariff(tariff)
